import React, { useCallback, useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import {
  PageHeader,
  CustomDropdownContainer,
} from "CommonComponent";
import {
  showCommonLoader,
  hideCommonLoader,
} from "actions/commonActions";
import { useObjectViewedId } from "hooks/useObjectViewedId";
import {
  GENERATE_AFTER_OPTIONS,
  REFERENCE_DATE_OPTIONS,
  PAF_FREQUENCY_OPTIONS,
} from "./constants";
import {
  fetchPafGenerationSettings,
  bulkUpdatePafGenerationSettings,
} from "./pafSettingsApi";
import "./PafSettings.css";

const SHOW_SNACKBAR = "SHOW_SNACKBAR";

const notify = (message, type = "general_error") => ({
  type: SHOW_SNACKBAR,
  snackbar_notification: message,
  snackbar_notification_type: type,
});

const getErrorMessage = (error) => {
  const response = error?.response;
  if (response?.status === 422) {
    const errors = response.data?.errors;
    if (errors && typeof errors === "object") {
      const firstKey = Object.keys(errors)[0];
      const firstMessage = firstKey ? errors[firstKey]?.[0] : null;
      if (firstMessage) return String(firstMessage);
    }
    return response.data?.message || "Validation failed.";
  }

  return (
    response?.data?.message ||
    response?.data?.error ||
    error?.message ||
    "Something went wrong."
  );
};

const PafSettings = () => {
  const dispatch = useDispatch();
  const objectViewedId = useObjectViewedId();
  const [generationRows, setGenerationRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    dispatch(showCommonLoader());

    try {
      const rows = await fetchPafGenerationSettings(objectViewedId);
      setGenerationRows(rows);
    } catch (error) {
      setGenerationRows([]);
      dispatch(notify(getErrorMessage(error)));
    } finally {
      setIsLoading(false);
      dispatch(hideCommonLoader());
    }
  }, [dispatch, objectViewedId]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSelectChange = (rowId, field) => (option) => {
    setGenerationRows((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, [field]: option } : row))
    );
  };

  const handleAutoGenerateChange = (rowId) => (event) => {
    setGenerationRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? { ...row, autoGenerate: event.target.checked }
          : row
      )
    );
  };

  const handleFrequencyToggle = (rowId, frequencyValue) => {
    setGenerationRows((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) return row;
        return {
          ...row,
          pafFrequencies: {
            ...row.pafFrequencies,
            [frequencyValue]: !row.pafFrequencies?.[frequencyValue],
          },
        };
      })
    );
  };

  const handleSave = async () => {
    if (isSaving || !canSave) return;

    setIsSaving(true);
    dispatch(showCommonLoader());

    try {
      const rows = await bulkUpdatePafGenerationSettings(
        generationRows,
        objectViewedId
      );
      setGenerationRows(rows);
      dispatch(notify("PAF generation settings saved successfully", "success"));
    } catch (error) {
      dispatch(notify(getErrorMessage(error)));
    } finally {
      setIsSaving(false);
      dispatch(hideCommonLoader());
    }
  };

  const canSave =
    !isLoading &&
    !isSaving &&
    generationRows.length > 0 &&
    generationRows.every(
      (row) =>
        row.id &&
        row.generateAfter?.value &&
        row.referenceDate?.value &&
        PAF_FREQUENCY_OPTIONS.some((option) =>
          Boolean(row.pafFrequencies?.[option.value])
        )
    );

  return (
    <div className="paf-settings-page">
      <PageHeader title="PAF Settings" />

      <div className="main-content-div page-card paf-settings-card">
        <div className="paf-settings-section-header">
          <div>
            <h2 className="paf-settings-section-title heading-color">
              PAF Generation Setup
            </h2>
            <p className="paf-settings-section-desc">
              Configure generation rules for each PAF type. Enable the frequencies
              that should run for that role.
            </p>
          </div>
          <button
            type="button"
            className="n-u-btn"
            onClick={handleSave}
            disabled={!canSave}
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>

        <div className="paf-settings-table-wrap">
          <table className="paf-settings-table">
            <thead>
              <tr>
                <th scope="col">PAF Type</th>
                <th scope="col">
                  Generate After <span className="paf-settings-required">*</span>
                </th>
                <th scope="col">
                  Reference Date <span className="paf-settings-required">*</span>
                </th>
                <th scope="col">
                  Frequency <span className="paf-settings-required">*</span>
                </th>
                <th scope="col" className="paf-settings-col-auto">
                  Auto Generate
                </th>
              </tr>
            </thead>
            <tbody>
              {!isLoading && generationRows.length === 0 ? (
                <tr>
                  <td colSpan={5}>
                    <p className="paf-settings-field-hint">
                      No PAF generation settings found.
                    </p>
                  </td>
                </tr>
              ) : (
                generationRows.map((row) => {
                  const typeLabel = row.pafTypeLabel || row.pafType;
                  const hasFrequency = PAF_FREQUENCY_OPTIONS.some((option) =>
                    Boolean(row.pafFrequencies?.[option.value])
                  );

                  return (
                    <tr key={row.id || row.pafType}>
                      <td data-label="PAF Type">
                        <span className="paf-settings-type">{typeLabel}</span>
                      </td>

                      <td data-label="Generate After">
                        <Select
                          name={`generate_after_${row.pafType}`}
                          options={GENERATE_AFTER_OPTIONS}
                          value={row.generateAfter}
                          onChange={handleSelectChange(row.id, "generateAfter")}
                          placeholder="Select duration"
                          className="multi-select w-100 paf-settings-select"
                          classNamePrefix="react-select"
                          isSearchable={false}
                          components={{ ValueContainer: CustomDropdownContainer }}
                          aria-label={`Generate PAF after for ${typeLabel}`}
                        />
                      </td>

                      <td data-label="Reference Date">
                        <Select
                          name={`reference_date_${row.pafType}`}
                          options={REFERENCE_DATE_OPTIONS}
                          value={row.referenceDate}
                          onChange={handleSelectChange(row.id, "referenceDate")}
                          placeholder="Select reference date"
                          className="multi-select w-100 paf-settings-select"
                          classNamePrefix="react-select"
                          isSearchable={false}
                          components={{ ValueContainer: CustomDropdownContainer }}
                          aria-label={`Reference date for ${typeLabel}`}
                        />
                      </td>

                      <td data-label="Frequency">
                        <div
                          className={`paf-settings-freq-group${
                            !hasFrequency ? " is-invalid" : ""
                          }`}
                          role="group"
                          aria-label={`PAF frequency for ${typeLabel}`}
                        >
                          {PAF_FREQUENCY_OPTIONS.map((option) => {
                            const isEnabled = Boolean(
                              row.pafFrequencies?.[option.value]
                            );

                            return (
                              <button
                                key={option.value}
                                type="button"
                                className={`paf-settings-freq-btn${
                                  isEnabled ? " is-on" : ""
                                }`}
                                aria-pressed={isEnabled}
                                onClick={() =>
                                  handleFrequencyToggle(row.id, option.value)
                                }
                              >
                                {option.label}
                              </button>
                            );
                          })}
                        </div>
                        {!hasFrequency && (
                          <p className="paf-settings-field-hint text-danger">
                            Select at least one frequency
                          </p>
                        )}
                      </td>

                      <td
                        data-label="Auto Generate"
                        className="paf-settings-col-auto"
                      >
                        <div className="paf-settings-switch-wrap">
                          <Form.Check
                            type="switch"
                            id={`paf-auto-generate-${row.pafType}`}
                            name={`auto_generate_${row.pafType}`}
                            checked={row.autoGenerate}
                            onChange={handleAutoGenerateChange(row.id)}
                            className="custom-switch"
                            aria-label={`Auto generate for ${typeLabel}`}
                          />
                          <span
                            className={`paf-settings-auto-label${
                              row.autoGenerate ? " is-on" : ""
                            }`}
                          >
                            {row.autoGenerate ? "On" : "Off"}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PafSettings;
